import { Controller, FieldValues, useController } from 'react-hook-form';
import Select from 'react-select';

import { Label } from '@/components/UI/Label/Label';
import { ActiviteitVoorBewegingModel } from '@/queries/queryModels/activiteitenVoorBewegingModel';
import { reactSelectOnBlurWorkaround } from '@/utils/helpers';

import { FieldError } from '../FieldError';
import { FieldProps } from '../types';

import styles from '../Fields.module.css';

type SelectFieldProps<T extends FieldValues> = FieldProps<T> & {
  testId?: string;
  minWidth?: string;
  shortInputField?: boolean;
  onChangeCallback?: (
    p:
      | string
      | number
      | ActiviteitVoorBewegingModel
      | {
          treinnummer: number;
          dienstregelVersie: number;
        }
      | null,
  ) => void;
  options?: { label: string; value: string | number | ActiviteitVoorBewegingModel }[];
} & Omit<React.ComponentPropsWithoutRef<typeof Select>, keyof FieldProps<T>>;

export const SelectField = <T extends FieldValues>({
  control,
  options,
  minWidth,
  shortInputField,
  onChangeCallback,
  name,
  label,
  subLabel,
  testId,
  required,
  rules,
  placeholder,
  defaultValue,
}: SelectFieldProps<T>) => {
  const { fieldState } = useController<T>({
    name,
    control,
    rules: { required: required ? 'Dit veld is verplicht.' : undefined, ...rules },
    defaultValue,
  });
  return (
    <div>
      <Label>{label}</Label>
      {subLabel && <small>{subLabel}</small>}
      <div
        style={{ minWidth: minWidth ?? '150px' }}
        className={`${styles.selectContainer} ${shortInputField ? 'reactSelectShortInputfield' : ''}`}
      >
        <Controller
          control={control}
          name={name}
          defaultValue={defaultValue}
          render={({ field }) => (
            <Select
              id={`normal-select-${testId}`}
              name={testId}
              placeholder={placeholder}
              className='react-select-container'
              classNamePrefix='react-select'
              inputId={testId}
              ref={field.ref}
              noOptionsMessage={() => 'Geen opties'}
              options={options}
              value={field.value ? options?.find((c) => JSON.stringify(c.value) === JSON.stringify(field.value)) : null}
              onChange={(val) => {
                field.onChange(val ? val.value : null);
                onChangeCallback?.(val ? val.value : null);
              }}
              onBlur={reactSelectOnBlurWorkaround}
            />
          )}
        />
        <FieldError width='auto' error={fieldState.error} />
      </div>
    </div>
  );
};
